import React, { useEffect, useState } from 'react';
import apiClient from '../api';
import {
    Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
    TextField, Switch, IconButton, Button, CircularProgress, Alert, Dialog,
    DialogTitle, DialogContent, DialogActions, Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { formatToman } from '../labels';
import { glass } from '../theme';

function AdminServices() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [addOpen, setAddOpen] = useState(false);
    const [newService, setNewService] = useState({ name: '', description: '', base_hourly_rate: 150000 });
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        apiClient.get('/api/admin-panel/services/')
            .then((res) => setServices(res.data))
            .catch(() => setError('خطا در دریافت لیست خدمات.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const startEdit = (svc) => {
        setEditingId(svc.id);
        setEditValues({ name: svc.name, description: svc.description, base_hourly_rate: svc.base_hourly_rate });
    };

    const saveEdit = async (id) => {
        setSaving(true);
        try {
            await apiClient.patch(`/api/admin-panel/services/${id}/`, editValues);
            setEditingId(null);
            load();
        } catch {
            setError('خطا در ذخیره‌ی تغییرات.');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (svc) => {
        try {
            await apiClient.patch(`/api/admin-panel/services/${svc.id}/`, { is_active: !svc.is_active });
            load();
        } catch {
            setError('خطا در تغییر وضعیت فعال بودن.');
        }
    };

    const deleteService = async (id) => {
        try {
            await apiClient.delete(`/api/admin-panel/services/${id}/`);
            load();
        } catch (e) {
            setError(e.response?.data?.detail || 'خطا در حذف خدمت.');
        }
    };

    const createService = async () => {
        setSaving(true);
        try {
            await apiClient.post('/api/admin-panel/services/', newService);
            setAddOpen(false);
            setNewService({ name: '', description: '', base_hourly_rate: 150000 });
            load();
        } catch {
            setError('خطا در ایجاد خدمت جدید.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>خدمات و قیمت‌گذاری</Typography>
                    <Typography color="text.secondary">
                        نرخ پایه‌ی هر خدمت مبنای موتور قیمت‌گذاری هوشمند است — تغییر آن بلافاصله روی برآوردهای جدید اثر می‌گذارد.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>خدمت جدید</Button>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Box sx={{ ...glass.card, overflow: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>نام خدمت</TableCell>
                            <TableCell>توضیحات</TableCell>
                            <TableCell>نرخ پایه (بر ساعت)</TableCell>
                            <TableCell>فعال</TableCell>
                            <TableCell>عملیات</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {services.map((svc) => (
                            <TableRow key={svc.id} hover>
                                {editingId === svc.id ? (
                                    <>
                                        <TableCell>
                                            <TextField size="small" value={editValues.name}
                                                onChange={(e) => setEditValues((v) => ({ ...v, name: e.target.value }))} />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 220 }}>
                                            <TextField size="small" fullWidth value={editValues.description}
                                                onChange={(e) => setEditValues((v) => ({ ...v, description: e.target.value }))} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" type="number" value={editValues.base_hourly_rate}
                                                onChange={(e) => setEditValues((v) => ({ ...v, base_hourly_rate: Number(e.target.value) }))}
                                                sx={{ width: 130 }} />
                                        </TableCell>
                                        <TableCell>
                                            <Switch checked={!!svc.is_active} onChange={() => toggleActive(svc)} />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" color="primary" disabled={saving} onClick={() => saveEdit(svc.id)}>
                                                <SaveIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell sx={{ fontWeight: 700 }}>{svc.name}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', maxWidth: 320 }}>{svc.description}</TableCell>
                                        <TableCell>{formatToman(svc.base_hourly_rate)} / ساعت</TableCell>
                                        <TableCell>
                                            <Switch checked={!!svc.is_active} onChange={() => toggleActive(svc)} />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => startEdit(svc)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={() => deleteService(svc.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>خدمت جدید</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="نام خدمت" value={newService.name}
                            onChange={(e) => setNewService((v) => ({ ...v, name: e.target.value }))} fullWidth />
                        <TextField label="توضیحات" value={newService.description} multiline rows={2}
                            onChange={(e) => setNewService((v) => ({ ...v, description: e.target.value }))} fullWidth />
                        <TextField label="نرخ پایه به ریال بر ساعت" type="number" value={newService.base_hourly_rate}
                            onChange={(e) => setNewService((v) => ({ ...v, base_hourly_rate: Number(e.target.value) }))} fullWidth />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>انصراف</Button>
                    <Button variant="contained" disabled={saving || !newService.name} onClick={createService}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'ایجاد'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminServices;
